export const UNIT_CONVERSION_FACTORS = Object.freeze({
  "gram/hectoliter": 10,
  "gram/kilogram": 1000,
  "gram/litre": 1000,
  "microgram/gram": 1,
  "microgram/kilogram": 0.001,
  "microgram/litre": 0.001,
  "microgram/millilitre": 1,
  "milligram/gram": 1000,
  "milligram/kilogram": 1,
  "milligram/litre": 1,
  "milligram/millilitre": 1000,
  "nanogram/gram": 0.001,
  "nanogram/kilogram": 0.000_001,
  "nanogram/litre": 0.000_001,
  "nanogram/millilitre": 0.001,
  "picogram/gram": 0.000_001,
  "picogram/kilogram": 0.000_000_001,
});

export const LCA_INDICATORS = Object.freeze([
  {
    label: "Global warming",
    definition: "Global warming potential due to GHG emissions",
    options: [
      "Stratospheric ozone depletion",
      "Ionizing radiation",
      "Ozone formation, Human health",
      "Fine particulate matter formation",
      "Ozone formation, Terrestrial ecosystems",
    ],
    processes: [
      "Direct emissions",
      "Ammonia Production",
      "Heat production",
      "Urea production",
      "Remaining processes",
      "Diesel burned in machinery",
    ],
    substances: ["Remaining substances", "Carbon dioxide, fossil", "Dinitrogen monoxide"],
  },
  {
    label: "Terrestrial acidification",
    definition: "Acidification due to air pollutants",
    options: [],
    processes: [],
    substances: ["Remaining substances", "Ammonia"],
  },
  {
    label: "Marine eutrophication",
    definition: "Nutrient enrichment (nitrogen) in marine systems",
    options: ["Marine eutrophication"],
    processes: [],
    substances: ["Remaining substances", "Nitrate"],
  },
  {
    label: "Terrestrial ecotoxicity",
    definition: "Ecotoxic effects on terrestrial ecosystems",
    options: [
      "Freshwater ecotoxicity",
      "Marine ecotoxicity",
      "Human carcinogenic toxicity",
      "Human non-carcinogenic toxicity",
    ],
    processes: [
      "Remaining processes",
      "Diesel production",
      "Fertilizer NPK production",
      "Tractor production",
      "Korn-kali production",
      "Direct emissions",
    ],
    substances: ["Remaining substances", "Copper, ion", "Flufenacet", "Folpet"],
  },
  {
    label: "Land use",
    definition: "Land occupation/transformation affecting ecosystems",
    options: ["Mineral resource scarcity", "Fossil resource scarcity"],
    processes: [],
    substances: ["Occupation, annual crop"],
  },
  {
    label: "Water consumption",
    definition: "Impacts from freshwater use affecting availability, health, and ecosystems",
    options: [],
    processes: [],
    substances: ["water, unspecified origin"],
  },
]);

export const ENV_IMPACT_CATEGORIES = Object.freeze([
  "Acidification",
  "Climate Change",
  "Climate Change - Biogenic",
  "Climate Change fossil (only Peat)",
  "Climate Change - Fossil (inc. Peat oxidation)",
  "Climate Change - Land Use and Land Use Change",
  "Climate Change excl LUC / peat oxidation",
  "Ecotoxicity, freshwater - part 1",
  "Ecotoxicity, freshwater - part 2",
  "Ecotoxicity, freshwater - inorganics",
  "Ecotoxicity, freshwater - organics - p.1",
  "Ecotoxicity, freshwater - organics - p.2",
  "Eutrophication, freshwater",
  "Eutrophication, marine",
  "Eutrophication, terrestrial",
  "Human toxicity, cancer",
  "Human toxicity, cancer - inorganics",
  "Human toxicity, cancer - organics",
  "Human toxicity, non-cancer",
  "Human toxicity, non-cancer - inorganics",
  "Human toxicity, non-cancer - organics",
  "Ionising radiation",
  "Land use",
  "Ozone depletion",
  "Particulate matter (disease inc.)",
  "Photochemical ozone formation",
  "Resource use, fossils",
  "Resource use, minerals and metals",
  "Water use",
]);

export const SOCIAL_INDICATORS = Object.freeze([
  "Access to Drinking Water",
  "Access to Electricity",
  "Access to Hospital Beds",
  "Access to Sanitation",
  "Child Labor",
  "Children out of School",
  "Communicable Diseases",
  "Corruption",
  "Democracy & Freedom of Speech",
  "Discrimination",
  "Excessive Work Time",
  "Forced Labor",
  "Freedom of Association",
  "Gender Equity",
  "High Conflict Zones",
  "Indigenous Rights",
  "Injuries & Fatalities",
  "Labor Laws/Convs",
  "Legal System",
  "Migrant Labor",
  "Non-Communicable Diseases",
  "Occ. Rsk & Haz",
  "Poverty and Inequality",
  "Property rights",
  "Smallholder v Commercial Farms",
  "Social Benefits",
  "State of Env Sustainability",
  "Unemployment",
  "Wage assessment",
  "Workers in poverty",
]);

export const SOCIAL_ENDPOINT_CATEGORIES = Object.freeze([
  "Labor rights & decent work",
  "Health & safety",
  "Society",
  "Governance",
  "Community",
]);

export const SOCIAL_ENDPOINT_CATEGORY_DEFINITIONS = Object.freeze({
  "Labor rights & decent work": "Assesses risks related to workers' rights, fair wages, working conditions, freedom of association, and protection against forced and child labor.",
  "Health & safety": "Evaluates occupational health and safety risks, including workplace injuries, fatalities, exposure to hazards, and access to healthcare.",
  "Society": "Covers broader societal impacts such as poverty, inequality, education access, corruption, and democratic freedoms.",
  "Governance": "Measures governance-related risks including legal system effectiveness, property rights, corruption levels, and regulatory frameworks.",
  "Community": "Assesses impacts on local communities including indigenous rights, environmental sustainability, and smallholder vs commercial farm dynamics.",
});

export const SOCIAL_INDICATOR_CATEGORY_MAP = Object.freeze({
  "Access to Drinking Water": "Health & safety",
  "Access to Electricity": "Society",
  "Access to Hospital Beds": "Health & safety",
  "Access to Sanitation": "Health & safety",
  "Child Labor": "Labor rights & decent work",
  "Children out of School": "Society",
  "Communicable Diseases": "Health & safety",
  "Corruption": "Governance",
  "Democracy & Freedom of Speech": "Governance",
  "Discrimination": "Labor rights & decent work",
  "Excessive Work Time": "Labor rights & decent work",
  "Forced Labor": "Labor rights & decent work",
  "Freedom of Association": "Labor rights & decent work",
  "Gender Equity": "Labor rights & decent work",
  "High Conflict Zones": "Society",
  "Indigenous Rights": "Community",
  "Injuries & Fatalities": "Health & safety",
  "Labor Laws/Convs": "Labor rights & decent work",
  "Legal System": "Governance",
  "Migrant Labor": "Labor rights & decent work",
  "Non-Communicable Diseases": "Health & safety",
  "Occ. Rsk & Haz": "Health & safety",
  "Poverty and Inequality": "Society",
  "Property rights": "Governance",
  "Smallholder v Commercial Farms": "Community",
  "Social Benefits": "Labor rights & decent work",
  "State of Env Sustainability": "Community",
  "Unemployment": "Society",
  "Wage assessment": "Labor rights & decent work",
  "Workers in poverty": "Labor rights & decent work",
});

export const SOCIAL_INDICATOR_DEFINITIONS = Object.freeze({
  "Access to Drinking Water": "Percentage of population with access to improved drinking water sources.",
  "Access to Electricity": "Percentage of population with access to electricity.",
  "Access to Hospital Beds": "Number of hospital beds per 10,000 inhabitants, reflecting healthcare infrastructure capacity.",
  "Access to Sanitation": "Percentage of population with access to improved sanitation facilities.",
  "Child Labor": "Prevalence of child labor in the workforce, measuring exploitation of minors.",
  "Children out of School": "Percentage of children of official school age not enrolled in education.",
  "Communicable Diseases": "Incidence of communicable diseases per capita, indicating public health risk.",
  "Corruption": "Level of perceived corruption in public institutions and private sector.",
  "Democracy &Freedom of Speech": "Degree of democratic governance and press freedom in the country.",
  "Discrimination": "Prevalence of workplace discrimination based on gender, ethnicity, or other factors.",
  "Excessive WkTime": "Proportion of workers exceeding standard working hours, indicating overwork risk.",
  "Forced Labor": "Prevalence of forced or compulsory labor in the economy.",
  "Freedom of Assoc": "Degree to which workers can freely form and join trade unions.",
  "Gender Equity": "Measure of gender-based inequality in economic participation, education, and health.",
  "High Conflict Zones": "Proximity to or involvement in armed conflict zones.",
  "Indigenous Rights": "Protection of indigenous peoples' rights and cultural heritage.",
  "Injuries & Fatalities": "Rate of workplace injuries and fatalities per worker.",
  "Labor Laws/Convs": "Strength and enforcement of labor laws and international conventions.",
  "Legal System": "Effectiveness and fairness of the legal system in enforcing rights.",
  "Migrant Labor": "Prevalence and treatment of migrant workers in the workforce.",
  "Non-Communicable Diseases": "Incidence of non-communicable diseases (e.g., cardiovascular, diabetes) per capita.",
  "Occ Tox & Haz": "Exposure to occupational risks and hazardous working conditions.",
  "Poverty and inequality": "Level of income poverty and wealth inequality in the population.",
  "Property rights": "Protection of private property rights and intellectual property.",
  "Smallholder v Commercial Farms": "Balance between smallholder and commercial farming operations.",
  "Social Benefits": "Availability and coverage of social security and welfare programs.",
  "State of Env Sustainability": "Commitment to environmental sustainability policies and practices.",
  "Unemployment": "Rate of unemployment in the workforce.",
  "Wage assessment": "Adequacy of wages relative to living standards and minimum wage laws.",
  "Workers in poverty": "Proportion of employed workers living below the poverty line.",
});
